import React, { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { addGoogleMapsPolygon, deleteGooglePolygon, getDigifarmMapsGeo } from "services";

import { formatNumber, getVariance, useCancelAsyncOnUnmount } from "utils";

import { GET_FIELDS_SHAPE_ADMIN } from "consts";

import { Card, Col, ErrorBoundary, Icon, MapInteractive, Row, Spin, Tooltip, Typography, Message as message } from "components";

export const FieldShapeVerification = ({
  field,
  farmId,
  geometries,
  overrideUserReportedGeometry,
  setLoading = () => {},
  showDrawing,
  markers,
  belowNoDigifarm,
  editIcon,
  tiny,
}) => {
  const [areas, setAreas] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const [loadingDigifarm, setLoadingDigifarm] = useState();
  const [hasDigifarmError, setHasDigifarmError] = useState(false);
  const queryClient = useQueryClient();

  // Used to rerender google map of drawn polygon when polygon is cleared
  const [geometriesVersion, setGeometriesVersion] = useState(0);

  useEffect(() => {
    setGeometriesVersion((prev) => prev + 1);
  }, [geometries]);

  const invalidateShapes = () => {
    queryClient.invalidateQueries([GET_FIELDS_SHAPE_ADMIN, farmId]);
  };

  const calculateFieldSize = (field) => {
    setLoadingDigifarm(true);
    setHasDigifarmError(false);
    getDigifarmMapsGeo({
      lat: field.location.lat,
      lng: field.location.lng,
      fieldId: field.id,
      farmId,
      signal,
    })
      .then(() => {
        message({
          content: "The digifarm gremlins accepted your sacrifice.",
          icon: "gremlin",
        });
        invalidateShapes();
      })
      .catch((_err) => setHasDigifarmError(true))
      .finally(() => {
        setLoadingDigifarm(false);
      });
  };

  const updateShapeArea = (type, field, area) => {
    const existingAreas = [...areas];
    const existingArea = existingAreas.find((area) => area.fieldId === field.id && area.type === type);
    if (!existingArea) {
      existingAreas.push({
        fieldId: field.id,
        type: type,
        area: area,
      });

      setAreas(existingAreas);
    }
  };

  const setupDrawingAPI = (map, google, field) => {
    if (google && google.drawing) {
      const drawingManager = new google.drawing.DrawingManager({
        drawingMode: google.drawing.OverlayType.POLYGON,
        drawingControl: false,
        polygonOptions: {
          strokeColor: "red",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "red",
          fillOpacity: 0.2,
          clickable: false,
          editable: false,
        },
      });

      google.event.addListener(drawingManager, "overlaycomplete", (e) => {
        if (e.type === google.drawing.OverlayType.POLYGON) {
          const shape = e.overlay;

          const coordinateArray = shape.getPath().getArray();
          const geometry = coordinateArray.map((item) => {
            const lat = item.lat();
            const lng = item.lng();

            return { lat: lat, lng: lng };
          });

          addGoogleMapsPolygon({
            geometry: geometry,
            fieldId: field.id,
            center: field.location,
            farmId,
            signal,
          })
            .then(() => {
              message({
                content: "The gremlins have accepted your polygon.",
                icon: "gremlin",
              });

              const area = google && google.geometry ? Math.round(google.geometry.spherical.computeArea(shape.getPath()) / 100) / 100 : 0;
              const existingArea = areas.find((item) => item.fieldId === field.id && item.type === "googlePolygon");
              if (!existingArea) {
                areas.push({
                  fieldId: field.id,
                  type: "googlePolygon",
                  area: area,
                });

                setAreas(areas);
              }
              invalidateShapes();
            })
            .finally(() => {
              setLoading?.(false);
            });

          // setDrawnArea(area);
        }
      });

      drawingManager.setMap(map);
    }
  };

  const handleClearPolygon = (fieldId) => {
    deleteGooglePolygon({
      fieldId: fieldId,
      signal,
    })
      .then(() => {
        message({
          content: "The gremlins have destroyed the existing polygon.",
          icon: "gremlin",
        });

        const newAreas = [...areas];
        const polygonIndex = newAreas.findIndex((item) => item.fieldId === fieldId && item.type === "googlePolygon");
        newAreas.splice(polygonIndex, 1);

        setAreas(newAreas);
        invalidateShapes();
      })
      .finally(() => {
        setLoading?.(false);
      });
  };

  const getArea = (field, shapeType) =>
    areas.find((item) => item.fieldId === field.id && item.type === shapeType)
      ? areas.find((item) => item.fieldId === field.id && item.type === shapeType).area
      : 0;

  return (
    <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]} className="margin-top-200">
      {/* prelint-ignore-col */}
      {field.location && (
        <Col sm={24} md={showDrawing ? 8 : 12}>
          <Typography variant="h4">Farmer reported size</Typography>
          <Typography variant="paragraph-m">
            {formatNumber({
              number: parseFloat(field.size),
              decimalPlaces: 4,
            })}
            ha
          </Typography>
          <Card className={`map-card  ${tiny ? "tiny" : ""}`}>
            {(overrideUserReportedGeometry || (geometries && geometries.find((item) => item.fieldId === field.id && item.type === "shapeFile"))) && (
              <ErrorBoundary>
                <MapInteractive
                  field={field}
                  geometry={overrideUserReportedGeometry || geometries.find((item) => item.fieldId === field.id && item.type === "shapeFile") || null}
                  location={field.location}
                  markers={markers}
                  scaleControl={true}
                  zoomControl={false}
                />
              </ErrorBoundary>
            )}
          </Card>
        </Col>
      )}
      {field.location && showDrawing && (
        <Col sm={24} md={8}>
          <Typography variant="h4">
            Drawing verification tool{" "}
            {geometries.some((item) => item.fieldId === field.id && item.type === "googlePolygon") && (
              <Tooltip title="Clear shape">
                <Icon iconName="tabler/x" className="icon inline pointer green-text" onClick={() => handleClearPolygon(field.id)} />
              </Tooltip>
            )}
          </Typography>
          <Typography variant="paragraph-m">
            {areas &&
            areas.length &&
            areas.find((item) => item.fieldId === field.id && item.type === "googlePolygon") &&
            geometries.find((item) => item.fieldId === field.id && item.type === "googlePolygon")
              ? formatNumber({
                  number: getArea(field, "googlePolygon"),
                  decimalPlaces: 4,
                })
              : 0}
            ha (
            {geometries.find((item) => item.fieldId === field.id && item.type === "googlePolygon") &&
            areas.find((item) => item.fieldId === field.id && item.type === "googlePolygon") &&
            getArea(field, "googlePolygon")
              ? getVariance(field.size, getArea(field, "googlePolygon"), 2)
              : 0}
            % variance)
          </Typography>
          <Card className={`map-card  ${tiny ? "tiny" : ""}`}>
            <ErrorBoundary>
              <MapInteractive
                field={field}
                geometry={geometries.find((item) => item.fieldId === field.id && item.type === "googlePolygon") || null}
                location={field.location}
                markers={markers}
                scaleControl={true}
                setupDrawingAPI={geometries.find((item) => item.fieldId === field.id && item.type === "googlePolygon") ? false : setupDrawingAPI}
                updateArea={(area) => updateShapeArea("googlePolygon", field, area)}
                zoomControl={false}
                key={geometriesVersion}
              />
            </ErrorBoundary>
          </Card>
        </Col>
      )}
      {field.location && (
        <Col sm={24} md={showDrawing ? 8 : 12}>
          <Typography variant="h4">{showDrawing ? "Digi farm" : "Verification tools"}</Typography>
          <Typography variant="paragraph-m">
            {areas && areas.length && areas.find((item) => item.fieldId === field.id && item.type === "digifarm")
              ? formatNumber({
                  number: getArea(field, "digifarm"),
                  decimalPlaces: 4,
                })
              : 0}
            ha ({getArea(field, "digifarm") ? getVariance(field.size, getArea(field, "digifarm"), 2) : 0}% variance)
          </Typography>
          {geometries && geometries.find((item) => item.fieldId === field.id && item.type === "digifarm") ? (
            <Card className={`map-card  ${tiny ? "tiny" : ""}`} id="staticMap">
              <ErrorBoundary>
                <MapInteractive
                  field={field}
                  geometry={geometries.find((item) => item.fieldId === field.id && item.type === "digifarm") || null}
                  location={field.location}
                  markers={markers}
                  scaleControl={true}
                  updateArea={(area) => updateShapeArea("digifarm", field, area)}
                  zoomControl={false}
                />
              </ErrorBoundary>
              {editIcon}
            </Card>
          ) : (
            <div className={`digifarm-wrapper ${tiny ? "tiny" : ""}`}>
              <Card className={`map-card digifarm-card pointer ${hasDigifarmError ? "error" : ""}`} id="staticMap" onClick={() => calculateFieldSize(field)}>
                {loadingDigifarm ? (
                  <Spin />
                ) : (
                  <>
                    <Icon size="xl" iconName={hasDigifarmError ? "tabler/photo-off" : "tabler/cloud-download"} />
                    <Typography variant="paragraph-l" className="center margin-top-100">
                      {hasDigifarmError ? "Something went wrong" : "Click to generate digifarm preview"}
                    </Typography>
                  </>
                )}
              </Card>
              {belowNoDigifarm}
            </div>
          )}
        </Col>
      )}
    </Row>
  );
};
